import { useState } from 'react';

type UseSelectionOptions<T> = {
   options: T[];
   initiallyDisabled: boolean;
   equalFunc?: (a: T, b: T) => boolean;
};

export function useSelection<T>(
   {
      options = [],
      initiallyDisabled = false,
      equalFunc = (a, b) => a === b,
   }: UseSelectionOptions<T> = {
      options: [],
      initiallyDisabled: false,
      equalFunc: (a, b) => a === b,
   }
) {
   const [isDisabled, setIsDisabled] = useState<boolean>(initiallyDisabled);

   const [selected, setSelected] = useState<T[]>([]);

   const optionSelected = (option: T) =>
      selected.find(selectedOption => equalFunc(option, selectedOption)) !== undefined;

   const allSelected = options.every(optionSelected);

   const someSelected = options.some(optionSelected);

   function toggleAll() {
      if (allSelected) {
         setSelected([]);
      } else {
         setSelected(options);
      }
   }

   function toggle(x: T) {
      if (optionSelected(x)) {
         setSelected(selected.filter(element => !equalFunc(x, element)));
      } else {
         setSelected([...selected, x]);
      }
   }

   function isSelected(x: T) {
      return optionSelected(x);
   }

   const isEnabled = !isDisabled;

   function disable() {
      setIsDisabled(true);
   }

   function enable() {
      setIsDisabled(false);
   }

   function clear() {
      setSelected([]);
   }

   const selection: Selection<T> = {
      toggle,
      toggleAll,
      clear,
      isSelected,
      disable,
      enable,
      isDisabled,
      isEnabled,
      allSelected,
      someSelected,
      selected,
      setSelected,
   };

   return selection;
}

export type Selection<T> = {
   toggle: (item: T) => void;
   toggleAll: () => void;
   clear: () => void;
   isSelected: (item: T) => boolean;
   disable: () => void;
   enable: () => void;
   isDisabled: boolean;
   isEnabled: boolean;
   allSelected: boolean;
   someSelected: boolean;
   selected: T[];
   setSelected: (selection: T[]) => void;
};
